// src/components/payment/ctl-payment.js
//
// Stripe Payment Element wrapper (client-side).
// Requirements:
// - You must create a PaymentIntent (or SetupIntent) on your server and pass its client_secret here.
// - This component loads https://js.stripe.com/v3/ once.

import { safeJsonParse } from '../../../utils/safeJsonParse.js';
//
// Attributes:
// - publishable-key (required): Stripe publishable key (pk_live_... / pk_test_...)
// - client-secret (required): PaymentIntent/SetupIntent client_secret
// - mode: "payment" | "setup" (default "payment") - used only for semantics/labels
// - appearance: JSON string (optional): Stripe appearance config
// - locale: Stripe locale (optional)
// - return-url: URL to redirect after confirm (optional; recommended for some methods)
// - button-label: text for built-in submit button (default "Pay now")
// - disabled: disables button + form
// - loading: shows skeleton-ish state (your UI can also manage this externally)
//
// Events:
// - ctl-ready (detail: { stripe, elements })
// - ctl-change (detail: Stripe change event payload)
// - ctl-error (detail: { error })
// - ctl-success (detail: { result })  // after confirm returns success (no redirect flows)
// - ctl-submit (detail: { mode })     // when user clicks submit (before confirm)
//
// Slots:
// - header (optional): top copy / title
// - footer (optional): below element (legal text, etc.)
// - actions (optional): override actions area (if present, built-in button is hidden)

import "../foundation/ctl-button.js";
import "../foundation/ctl-typography.js";
import "../foundation/ctl-skeleton.js";

const STRIPE_SRC = "https://js.stripe.com/v3/";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --text: var(--ctl-color-text);
    --muted: var(--ctl-color-muted);
    --surface: var(--ctl-color-surface);
    --border: var(--ctl-color-border);
    --radius: var(--ctl-radius-lg, 14px);
    --shadow: var(--ctl-shadow-sm, 0 6px 18px rgba(0,0,0,.10));
    --focus: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    font-family: var(--font);
    color: var(--text);
  }

  .frame{
    border: 1px solid var(--border);
    background: var(--surface);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    overflow:hidden;
  }

  .head{
    padding: var(--spacing-sm, 12px);
    border-bottom: var(--border-width-thin, 1px) solid var(--border);
  }
  .head:empty{ display:none; }

  .body{
    padding: var(--spacing-sm, 12px);
    display:grid;
    gap: var(--spacing-sm, 12px);
  }

  .hint{
    color: var(--muted);
    font-size: var(--font-size-xs, 0.75rem);
    line-height: var(--line-height-tight, 1.35);
  }

  .element{
    border: var(--border-width-thin, 1px) solid color-mix(in srgb, var(--border) 80%, transparent);
    border-radius: var(--spacing-sm, 12px);
    padding: var(--spacing-sm, 12px);
    background: var(--surface);
  }

  .actions{
    display:flex;
    gap: var(--spacing-xs, 10px);
    flex-wrap: wrap;
    justify-content:flex-end;
    align-items:center;
  }
  .actions:empty{ display:none; }

  .footer{
    padding: var(--spacing-sm, 12px) var(--spacing-sm, 14px) var(--spacing-sm, 14px);
    border-top: var(--border-width-thin, 1px) solid var(--border);
    color: var(--muted);
    font-size: var(--font-size-xs, 0.75rem);
    line-height: var(--line-height-tight, 1.35);
  }
  .footer:empty{ display:none; }

  :host([disabled]){ opacity: var(--opacity-70, 0.7); pointer-events:none; }

  /* loading state */
  .loading{
    display:none;
    padding: var(--spacing-sm, 14px);
    gap: var(--spacing-xs, 10px);
  }
  :host([loading]) .loading{ display:grid; }
  :host([loading]) .body{ display:none; }

  ::slotted(*){ max-width: 100%; }
</style>

<div class="frame" part="frame">
  <div class="head" part="header"><slot name="header"></slot></div>

  <div class="loading" part="loading">
    <ctl-skeleton variant="rect" height="14px" width="55%"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="12px" width="80%"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="120px"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="40px" width="35%"></ctl-skeleton>
  </div>

  <div class="body" part="body">
    <div class="element" part="element" id="mount"></div>

    <div class="hint" part="hint">
      Secure payment powered by Stripe.
    </div>

    <div class="actions" part="actions">
      <slot name="actions"></slot>
      <ctl-button id="payBtn"></ctl-button>
    </div>
  </div>

  <div class="footer" part="footer"><slot name="footer"></slot></div>
</div>
`;

function loadStripeScriptOnce() {
  // singleton promise
  if (window.__ctlStripeLoadPromise) return window.__ctlStripeLoadPromise;

  window.__ctlStripeLoadPromise = new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);

    const existing = document.querySelector(`script[src="${STRIPE_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Stripe));
      existing.addEventListener("error", reject);
      return;
    }

    const s = document.createElement("script");
    s.src = STRIPE_SRC;
    s.async = true;
    s.addEventListener("load", () => resolve(window.Stripe));
    s.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")));
    document.head.appendChild(s);
  });

  return window.__ctlStripeLoadPromise;
}

export class CtlPayment extends HTMLElement {
  static get observedAttributes() {
    return [
      "publishable-key",
      "client-secret",
      "mode",
      "appearance",
      "locale",
      "return-url",
      "button-label",
      "disabled",
      "loading"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._mount = this.shadowRoot.getElementById("mount");
    this._btn = this.shadowRoot.getElementById("payBtn");
    this._actionsSlot = this.shadowRoot.querySelector('slot[name="actions"]');

    this._stripe = null;
    this._elements = null;
    this._paymentEl = null;

    this._onSlotChange = this._onSlotChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("mode")) this.setAttribute("mode", "payment");
    if (!this.hasAttribute("button-label")) this.setAttribute("button-label", this.mode === "setup" ? "Save payment method" : "Pay now");

    this._actionsSlot.addEventListener("slotchange", this._onSlotChange);
    this._btn.addEventListener("click", this._onClick);

    this._syncButtonVisibility();
    this._syncButtonLabel();

    // Try to init if keys are present
    this.init().catch((err) => this._emitError(err));
  }

  disconnectedCallback() {
    this._actionsSlot.removeEventListener("slotchange", this._onSlotChange);
    this._btn.removeEventListener("click", this._onClick);
    this.destroy();
  }

  attributeChangedCallback(name) {
    if (name === "mode" || name === "button-label") this._syncButtonLabel();
    if (name === "publishable-key" || name === "client-secret" || name === "appearance" || name === "locale") {
      // re-init when key inputs change
      this.init().catch((err) => this._emitError(err));
    }
    if (name === "disabled" || name === "loading") this._syncDisabled();
  }

  // ---- Attributes ----
  get publishableKey() { return this.getAttribute("publishable-key") || ""; }
  set publishableKey(v) { this.setAttribute("publishable-key", String(v ?? "")); }

  get clientSecret() { return this.getAttribute("client-secret") || ""; }
  set clientSecret(v) { this.setAttribute("client-secret", String(v ?? "")); }

  get mode() { return this.getAttribute("mode") || "payment"; }
  set mode(v) { this.setAttribute("mode", v === "setup" ? "setup" : "payment"); }

  get appearance() {
    const str = this.getAttribute("appearance");
    return str ? safeJsonParse(str, null) : null;
  }
  set appearance(v) {
    if (v == null) this.removeAttribute("appearance");
    else this.setAttribute("appearance", JSON.stringify(v));
  }

  get locale() { return this.getAttribute("locale") || null; }
  set locale(v) { v == null ? this.removeAttribute("locale") : this.setAttribute("locale", String(v)); }

  get returnUrl() { return this.getAttribute("return-url") || null; }
  set returnUrl(v) { v == null ? this.removeAttribute("return-url") : this.setAttribute("return-url", String(v)); }

  get buttonLabel() { return this.getAttribute("button-label") || ""; }
  set buttonLabel(v) { this.setAttribute("button-label", String(v ?? "")); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled", "") : this.removeAttribute("disabled"); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  // ---- Public API ----
  async init() {
    // Don’t init if missing required attrs
    if (!this.publishableKey || !this.clientSecret) return;

    // If already initialized with same inputs, keep it
    const sig = JSON.stringify({
      pk: this.publishableKey,
      cs: this.clientSecret,
      appearance: this.appearance,
      locale: this.locale
    });
    if (this._sig === sig && this._stripe && this._elements && this._paymentEl) {
      this._syncDisabled();
      return;
    }
    this._sig = sig;

    this.loading = true;

    const StripeCtor = await loadStripeScriptOnce();
    if (!StripeCtor) throw new Error("Stripe.js loaded but window.Stripe is missing.");

    // Tear down previous if any
    this.destroy();

    this._stripe = StripeCtor(this.publishableKey);

    const elementsOpts = { clientSecret: this.clientSecret };
    if (this.appearance) elementsOpts.appearance = this.appearance;
    if (this.locale) elementsOpts.locale = this.locale;

    this._elements = this._stripe.elements(elementsOpts);

    this._paymentEl = this._elements.create("payment");
    this._paymentEl.on("change", (e) => {
      this.dispatchEvent(new CustomEvent("ctl-change", { bubbles: true, composed: true, detail: e }));
      // Disable button if incomplete
      const shouldDisable = !!e?.complete === false || !!e?.empty === true;
      this._btn.toggleAttribute("disabled", shouldDisable || this.disabled || this.loading);
    });

    this._mount.innerHTML = "";
    this._paymentEl.mount(this._mount);

    this.loading = false;
    this._syncDisabled();

    this.dispatchEvent(new CustomEvent("ctl-ready", {
      bubbles: true,
      composed: true,
      detail: { stripe: this._stripe, elements: this._elements }
    }));
  }

  async confirm({ returnUrl } = {}) {
    if (!this._stripe || !this._elements) throw new Error("ctl-payment not ready (missing Stripe Elements).");
    if (!this.clientSecret) throw new Error("Missing client-secret.");

    this.dispatchEvent(new CustomEvent("ctl-submit", {
      bubbles: true,
      composed: true,
      detail: { mode: this.mode }
    }));

    this.loading = true;
    this._syncDisabled();

    const finalReturnUrl = returnUrl || this.returnUrl || window.location.href;

    // Payment Element confirm. Some methods may redirect.
    const result = await this._stripe.confirmPayment({
      elements: this._elements,
      confirmParams: { return_url: finalReturnUrl },
      redirect: "if_required"
    });

    this.loading = false;
    this._syncDisabled();

    if (result?.error) {
      this._emitError(result.error);
      return { ok: false, error: result.error };
    }

    this.dispatchEvent(new CustomEvent("ctl-success", {
      bubbles: true,
      composed: true,
      detail: { result }
    }));

    return { ok: true, result };
  }

  destroy() {
    try { this._paymentEl?.destroy?.(); } catch {}
    this._paymentEl = null;
    this._elements = null;
    this._stripe = null;
    this._mount && (this._mount.innerHTML = "");
  }

  // ---- Internals ----
  _onSlotChange() {
    this._syncButtonVisibility();
  }

  _syncButtonVisibility() {
    const assigned = this._actionsSlot.assignedNodes({ flatten: true }) || [];
    const hasCustomActions = assigned.some((n) => n.nodeType === Node.ELEMENT_NODE || (n.nodeType === Node.TEXT_NODE && String(n.textContent || "").trim()));
    // If user provides any custom actions, hide built-in button.
    this._btn.style.display = hasCustomActions ? "none" : "";
  }

  _syncButtonLabel() {
    const provided = (this.getAttribute("button-label") || "").trim();
    if (provided) {
      this._btn.textContent = provided;
      return;
    }
    this._btn.textContent = this.mode === "setup" ? "Save payment method" : "Pay now";
  }

  _syncDisabled() {
    this._btn.toggleAttribute("disabled", this.disabled || this.loading);
    this._btn.toggleAttribute("loading", this.loading);
  }

  async _onClick(e) {
    e.preventDefault();
    if (this.disabled || this.loading) return;

    try {
      await this.confirm();
    } catch (err) {
      this.loading = false;
      this._syncDisabled();
      this._emitError(err);
    }
  }

  _emitError(error) {
    this.dispatchEvent(new CustomEvent("ctl-error", {
      bubbles: true,
      composed: true,
      detail: { error }
    }));
  }
}

customElements.define("ctl-payment", CtlPayment);
